import { fetchAuthSession } from 'aws-amplify/auth'
import { env } from '../../config/env'
import type { CallTelemetryEvent } from '../../debug/call-flow-log'

/**
 * Transport for the live call-telemetry pipeline: ships batched call+audio
 * events to the authenticated `recordCallTelemetry` AppSync mutation.
 *
 * Why a raw fetch instead of the shared appsync-client (generateClient)?
 *  - It must survive page teardown: on `visibilitychange`/`pagehide` we need
 *    `fetch(..., { keepalive: true })`, which Amplify's client does not expose.
 *  - It must be fire-and-forget and NEVER throw into the call path.
 *
 * It still uses the app's real Cognito session (the id token from Amplify) and
 * the deployed AppSync endpoint — auth config is untouched. The last id token is
 * cached so the unload flush can build a keepalive request without awaiting a
 * fresh session read (which may not resolve before the page is gone).
 */
const MUTATION = `mutation RecordCallTelemetry($events: [CallTelemetryEventInput!]!) {
  recordCallTelemetry(events: $events)
}`

let cachedToken: string | null = null

function toVariables(events: CallTelemetryEvent[]): { events: unknown[] } {
  return {
    events: events.map((e) => ({
      ts: e.ts,
      bootId: e.bootId,
      callId: e.callId ?? null,
      userId: e.userId ?? null,
      deviceId: e.deviceId,
      platform: e.platform,
      event: e.event,
      // AWSJSON expects a JSON string.
      data: e.data ? safeStringify(e.data) : null,
      seq: e.seq,
    })),
  }
}

function safeStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value)
  } catch {
    return null
  }
}

/** Sign-out: forget the previous account's id token used by unload flushes. */
export function resetCallTelemetryToken(): void {
  cachedToken = null
}

async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() ?? null
    if (token) cachedToken = token
    return token
  } catch {
    return cachedToken
  }
}

/**
 * Ship a batch of telemetry events. Resolves true when the endpoint accepted
 * them (so the caller can drop them), false on any failure (so the caller can
 * re-queue). Never throws.
 */
export async function shipCallTelemetry(events: CallTelemetryEvent[]): Promise<boolean> {
  if (!env.appsyncGraphqlUrl || events.length === 0) return false
  const token = await getIdToken()
  if (!token) return false
  try {
    const res = await fetch(env.appsyncGraphqlUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ query: MUTATION, variables: toVariables(events) }),
    })
    if (!res.ok) return false
    const json = (await res.json()) as { errors?: unknown[] }
    return !(Array.isArray(json.errors) && json.errors.length > 0)
  } catch {
    return false
  }
}

/**
 * Best-effort synchronous-ish flush for `pagehide`/`visibilitychange`, using a
 * keepalive request so the browser completes it even as the page unloads. Uses
 * the cached id token (does not await a fresh session). Returns immediately;
 * the request continues in the background.
 */
export function shipCallTelemetryOnUnload(events: CallTelemetryEvent[]): void {
  if (!env.appsyncGraphqlUrl || events.length === 0 || !cachedToken) return
  try {
    void fetch(env.appsyncGraphqlUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: cachedToken },
      body: JSON.stringify({ query: MUTATION, variables: toVariables(events) }),
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* unload path — nothing more we can do */
  }
}

/**
 * Warm the cached id token so the unload flush has something to send. Called
 * once telemetry starts (e.g. when a call begins). Never throws.
 */
export async function primeCallTelemetryAuth(): Promise<void> {
  await getIdToken()
}
